import React from "react";
import {
    Collapse,
    createStyles,
    Fab,
    Theme,
    Typography,
    withStyles,
    WithStyles
} from "@material-ui/core";
import posthog from "posthog-js";
import { useApprovedAuctionBidder } from "../../../hooks/useApprovedAuctionBidder";
import { useSignalR } from "../../../hooks/useSignalR";
import { BidLoadState } from "../../../models/utility/LoadState";
import { Numbers } from "../../../models/utility/Numbers";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import * as ParcelBidStore from "../../../store/ParcelBidStore";
import * as UserStore from "../../../store/UserStore";

type PlaceBidButtonProps = WithStyles<typeof styles> & {
    bidIncrement: number;
    canPlaceBid: boolean;
    isLive: boolean;
    parcelId: string;
    lastAcceptedBid: number;
};

const styles = (theme: Theme) =>
    createStyles({
        bidPopup: {
            [theme.breakpoints.down("sm")]: {
                paddingLeft: theme.spacing(3),
                paddingRight: theme.spacing(3)
            },
            display: "inline-block",
            paddingLeft: theme.spacing(),
            paddingRight: theme.spacing()
        },
        bidPopupClosed: {
            [theme.breakpoints.down("xs")]: {
                paddingLeft: `${theme.spacing()}px !important`,
                paddingRight: `${theme.spacing()}px !important`
            },
            paddingLeft: `${theme.spacing(2)}px !important`,
            paddingRight: `${theme.spacing(2)}px !important`
        },
        bidPopupHighest: {
            backgroundColor: theme.palette.success.main,
            color: theme.palette.success.contrastText
        },
        bidPopupOutbid: {
            backgroundColor: theme.palette.background.overlay,
            color: theme.palette.common.white
        },
        fabButtonBid: {
            [theme.breakpoints.down("sm")]: {
                paddingLeft: theme.spacing(9),
                paddingRight: theme.spacing(9)
            },
            display: "block",
            height: theme.spacing(4),
            paddingLeft: theme.spacing(6),
            paddingRight: theme.spacing(6),
            color: theme.palette.common.black
        },
        fabButtonBidWrapper: {
            display: "inline-block",
            textAlign: "center",
            paddingBottom: theme.spacing()
        }
    });

function PlaceBidButton({
    bidIncrement,
    canPlaceBid,
    classes,
    parcelId,
    lastAcceptedBid
}: PlaceBidButtonProps) {
    const dispatch = useAppDispatch();
    const { myBidIds, parcelBidDict, user } = useAppSelector((state) => ({
        myBidIds: state.parcelBidStoreState.myBidIds,
        parcelBidDict: state.parcelBidStoreState.parcelBidDict,
        user: state.userStoreState.user
    }));

    const approvedAuctionBidder = useApprovedAuctionBidder();
    const { isSignalRConnected } = useSignalR();

    const parcelBids = parcelBidDict[parcelId] || [];
    const hasPlacedBid = parcelBids.some((bid) =>
        ParcelBidStore.isMyBid(myBidIds, bid)
    );
    const highestBidder = ParcelBidStore.holdsHighBid(myBidIds, parcelBids);

    const bidCount = parcelBids.length;

    const newBidIncrement = bidCount > 0 ? bidIncrement : 0;
    const nextBid = lastAcceptedBid + newBidIncrement;

    const testId = `placeBidButton-${parcelId}`;

    const openBidDialog = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        dispatch(ParcelBidStore.actionCreators.toggleDialogVisible(true));
        dispatch(
            ParcelBidStore.actionCreators.updateCurrentBid(
                parcelId,
                BidLoadState.NotSubmitted
            )
        );
    };

    const redirect = () => {
        posthog.capture("ParcelPanel.LoginToBidClicked");
        return dispatch(UserStore.actionCreators.toggleRedirectToLogin(true));
    };

    const isDisabled =
        highestBidder ||
        !canPlaceBid ||
        !isSignalRConnected ||
        !approvedAuctionBidder.isApproved;

    return (
        <div className={classes.fabButtonBidWrapper}>
            {user && (
                <>
                    <Collapse
                        in={hasPlacedBid && !highestBidder}
                        mountOnEnter={true}
                        unmountOnExit={true}
                    >
                        <Typography
                            className={`${classes.bidPopup} ${classes.bidPopupOutbid}`}
                            component="div"
                            variant="caption"
                        >
                            You were outbid!
                        </Typography>
                    </Collapse>
                    <Collapse
                        in={hasPlacedBid && highestBidder}
                        mountOnEnter={true}
                        unmountOnExit={true}
                    >
                        <Typography
                            className={`${classes.bidPopup} ${classes.bidPopupHighest}`}
                            component="div"
                            variant="caption"
                        >
                            You have highest bid!
                        </Typography>
                    </Collapse>
                    <Fab
                        data-testid={testId}
                        className={classes.fabButtonBid}
                        disabled={isDisabled}
                        variant="extended"
                        color="primary"
                        onClick={openBidDialog}
                    >
                        Bid {Numbers.toShortFormat(nextBid)}
                    </Fab>
                </>
            )}
            {!user && (
                <Fab
                    className={classes.fabButtonBid}
                    variant="extended"
                    color="primary"
                    onClick={redirect}
                >
                    Login to Bid
                </Fab>
            )}
        </div>
    );
}

export default withStyles(styles)(PlaceBidButton);
